import React, { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, MessageSquareText, ShieldCheck, Smartphone } from 'lucide-react';
import ConversationList from './ConversationList';
import ConversationThread from './ConversationThread';

const DRIVER_FILTERS = [
  ['all', 'Activas'],
  ['unread', 'Sin leer'],
  ['delivery_issue', 'Incidencias'],
];

export default function DriverCommunicationView({ userId, setActiveComponent, initialConversationId }) {
  const [selected, setSelected] = useState(null);
  const [threadConversation, setThreadConversation] = useState(null);
  const isSupportConversation = (threadConversation?.type || selected?.type) === 'support_general';

  const handleSelect = useCallback((conversation) => {
    setSelected(conversation);
    setThreadConversation(null);
  }, []);

  const handleConversationLoaded = useCallback((conversation) => {
    setThreadConversation(conversation || null);
  }, []);

  useEffect(() => {
    if (!initialConversationId) return;
    setThreadConversation(null);
    setSelected(prev => (
      Number(prev?.id) === Number(initialConversationId)
        ? prev
        : { id: Number(initialConversationId) }
    ));
  }, [initialConversationId]);

  return (
    <div className="flex h-[100dvh] w-screen flex-col overflow-hidden bg-slate-100 sm:h-screen">
      <div className="flex items-center gap-3 border-b border-slate-200 bg-white px-3 py-3 sm:px-4">
        <button
          onClick={() => setActiveComponent?.('OptionsDriver')}
          className="inline-flex flex-shrink-0 items-center gap-2 rounded-lg bg-slate-100 px-3 py-2 text-sm font-bold text-slate-700 hover:bg-slate-200"
        >
          <ArrowLeft className="h-4 w-4" />
          Menú
        </button>
        <div className="min-w-0">
          <h1 className="truncate text-base font-extrabold text-slate-950 font-montserrat sm:text-lg">Chat Fast Max</h1>
          <p className="line-clamp-2 text-xs text-slate-500">
            Coordinación por entregas activas. Usa el botón flotante Fast Max para soporte directo con administradores.
          </p>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <div className={`${selected ? 'hidden sm:block' : 'block'} w-full sm:w-[430px]`}>
          <ConversationList
            scope="driver"
            userId={userId}
            selectedConversationId={selected?.id}
            onSelect={handleSelect}
            title="Chat Fast Max"
            subtitle="Entregas activas"
            filterOptions={DRIVER_FILTERS}
            extraParams={{ include_support: 0 }}
            limit={25}
            searchPlaceholder="Buscar entrega, tienda, cliente o direccion"
          />
        </div>
        <div className={`${selected ? 'flex' : 'hidden sm:flex'} min-h-0 min-w-0 flex-1 flex-col gap-2 p-2 sm:gap-3 sm:p-4`}>
          {selected && (
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="inline-flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-sm font-bold text-slate-700 shadow-sm ring-1 ring-slate-200 hover:bg-slate-50 sm:hidden"
            >
              <ArrowLeft className="h-4 w-4" />
              Conversaciones
            </button>
          )}
          <div className="hidden gap-3 md:grid md:grid-cols-3">
            <div className="rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-600 shadow-sm">
              <div className="mb-1 flex items-center gap-2 font-bold text-slate-900">
                <MessageSquareText className="h-4 w-4 text-[#B1336E]" />
                Hilo compartido
              </div>
              Fast Max, tienda/proveedor y motorizado pueden coordinar aquí por la entrega.
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-600 shadow-sm">
              <div className="mb-1 flex items-center gap-2 font-bold text-slate-900">
                <Smartphone className="h-4 w-4 text-emerald-600" />
                WhatsApp externo
              </div>
              El motorizado no envía plantillas ni WhatsApp API; solo verá esos mensajes registrados.
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-600 shadow-sm">
              <div className="mb-1 flex items-center gap-2 font-bold text-slate-900">
                <ShieldCheck className="h-4 w-4 text-sky-600" />
                Fast Max flotante
              </div>
              Usa el botón flotante Fast Max para soporte directo sin vincularlo a una entrega.
            </div>
          </div>

          <ConversationThread
            conversationId={selected?.id}
            currentUserId={userId}
            currentUserType="driver"
            className="min-h-0 flex-1"
            allowNotes={false}
            inputPlaceholder={isSupportConversation ? 'Escribe a Fast Max Admin...' : 'Escribe al hilo interno de esta entrega...'}
            inputHelperText={isSupportConversation
              ? 'Mensaje interno directo para Fast Max Admin.'
              : 'Mensaje interno: visible para Fast Max y la tienda/proveedor participante. No usa WhatsApp.'}
            onConversationLoaded={handleConversationLoaded}
            enableDriverCustomerChannels
          />
        </div>
      </div>
    </div>
  );
}
